import type Konva from "konva";
import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";
import {
  Circle,
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
  Text,
  Transformer,
} from "react-konva";

import {
  bakeScale,
  flatPoints,
  intersects,
  normalizeRect,
  regionBounds,
  simplifyPoints,
  toRelativePoints,
  type Rect as Box,
} from "../editor/geometry";
import { cellCounts, cellEdges } from "../editor/mosaic";
import { editableRegions } from "../editor/regions";
import type {
  EditableRegion,
  EditorImage,
  MosaicRegion,
  Point,
  RegionEffect,
  ShapeKind,
  ToolMode,
} from "../editor/types";
import { RegionNode, SELECTION_COLOR, type OutlineState } from "./canvas/RegionNode";
import { clampZoom, useViewport } from "./canvas/useViewport";

export interface RestorePreview {
  dataUrl: string;
  x: number;
  y: number;
  width: number;
  height: number;
  strength: number;
  compare: number;
}

export interface EditorCanvasHandle {
  finishPolygon: () => void;
  cancelPolygon: () => void;
  undoPolygonPoint: () => void;
}

export type SelectMode = "replace" | "toggle" | "add";

interface EditorCanvasProps {
  ref?: Ref<EditorCanvasHandle>;
  image: EditorImage;
  rendered: ImageBitmap | null;
  revision: number;
  tool: ToolMode;
  shapeKind: ShapeKind;
  penWidth: number;
  effect: RegionEffect;
  showOutlines: boolean;
  showGrid: boolean;
  gridSize: number;
  preview: boolean;
  comparing: boolean;
  selectedIds: string[];
  zoom: number;
  fitRequest: number;
  brushPreview: boolean;
  restorePreview: RestorePreview | null;
  topInset: number;
  onZoomChange: (zoom: number) => void;
  onSelect: (ids: string[], mode: SelectMode) => void;
  onAddRegion: (region: MosaicRegion) => void;
  onDraftChange: (region: MosaicRegion | null) => void;
  onBeginEdit: () => void;
  onUpdateRegions: (regions: EditableRegion[], final: boolean) => void;
  onRequestRestore: (rect: Box) => void;
  onPolygonChange: (vertexCount: number) => void;
}

type Gesture =
  | { kind: "pan"; origin: Point; startPan: Point }
  | { kind: "marquee"; start: Point; end: Point; mode: SelectMode }
  | { kind: "box"; start: Point; end: Point; square: boolean }
  | { kind: "brush"; points: Point[] }
  | { kind: "lasso"; origin: Point; points: Point[]; dragging: boolean }
  | { kind: "vertex"; origin: Point };

/** Screen distance a press may travel and still count as a click. */
const CLICK_SLOP = 5;
/** Screen radius around the first vertex that closes a polygon. */
const CLOSE_RADIUS = 10;

function useHtmlImage(url: string | undefined) {
  const [loaded, setLoaded] = useState<{ url: string; element: HTMLImageElement } | null>(null);
  useEffect(() => {
    if (!url) return;
    const element = new Image();
    element.decoding = "async";
    element.src = url;
    let active = true;
    element
      .decode()
      .then(() => {
        if (active) setLoaded({ url, element });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [url]);
  return loaded && loaded.url === url ? loaded.element : null;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function pointsOfNode(node: Konva.Shape): Point[] {
  const flat = (node as Konva.Line).points();
  const points: Point[] = [];
  for (let index = 0; index < flat.length; index += 2) {
    points.push({ x: flat[index], y: flat[index + 1] });
  }
  return points;
}

function polygonFrom(points: Point[], effect: RegionEffect): EditableRegion {
  const relative = toRelativePoints(points);
  return {
    id: crypto.randomUUID(),
    kind: "polygon",
    x: relative.origin.x,
    y: relative.origin.y,
    rotation: 0,
    effect,
    points: relative.points,
  };
}

export function EditorCanvas({
  ref,
  image,
  rendered,
  revision,
  tool,
  shapeKind,
  penWidth,
  effect,
  showOutlines,
  showGrid,
  gridSize,
  preview,
  comparing,
  selectedIds,
  zoom,
  fitRequest,
  brushPreview,
  restorePreview,
  topInset,
  onZoomChange,
  onSelect,
  onAddRegion,
  onDraftChange,
  onBeginEdit,
  onUpdateRegions,
  onRequestRestore,
  onPolygonChange,
}: EditorCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const brushRingRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 1, height: 1 });
  const [gesture, setGestureState] = useState<Gesture | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const [vertices, setVertices] = useState<Point[]>([]);
  const [hover, setHover] = useState<Point | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [pointerInside, setPointerInside] = useState(false);
  const [spaceDown, setSpaceDown] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);
  const touchesRef = useRef(new Map<number, Point>());
  const pinchRef = useRef<{ distance: number; center: Point; zoom: number; pan: Point } | null>(
    null,
  );
  const groupDragRef = useRef<{ origin: Point; positions: Map<string, Point> } | null>(null);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<EditableRegion[]>([]);

  const sourceImage = useHtmlImage(image.sourceUrl);
  const restoredImage = useHtmlImage(restorePreview?.dataUrl);
  const shownImage = comparing ? sourceImage : (rendered ?? sourceImage);

  const { view, pan, setPan, toImage, zoomAround } = useViewport({
    size,
    imageWidth: image.width,
    imageHeight: image.height,
    zoom,
    topInset,
    resetKey: `${image.id}:${fitRequest}`,
    onZoomChange,
  });

  const regions = useMemo(() => editableRegions(image.regions), [image.regions]);
  const regionsById = useMemo(
    () => new Map(regions.map((region) => [region.id, region])),
    [regions],
  );
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const editing = !preview && !comparing;
  const selecting = editing && tool === "select";

  const setGesture = (next: Gesture | null) => {
    gestureRef.current = next;
    setGestureState(next);
  };

  /* ---- layout & keyboard ------------------------------------------------ */

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({
        width: Math.max(1, Math.floor(entry.contentRect.width)),
        height: Math.max(1, Math.floor(entry.contentRect.height)),
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleDown = (event: KeyboardEvent) => {
      if (event.key === "Shift") setShiftDown(true);
      // Space pans unless a control has focus and would treat it as a press.
      if (event.code === "Space" && !event.repeat) {
        const target = event.target;
        if (
          target === document.body ||
          (target instanceof Node && containerRef.current?.contains(target))
        ) {
          event.preventDefault();
          setSpaceDown(true);
        }
      }
    };
    const handleUp = (event: KeyboardEvent) => {
      if (event.key === "Shift") setShiftDown(false);
      if (event.code === "Space") setSpaceDown(false);
    };
    const handleBlur = () => {
      setShiftDown(false);
      setSpaceDown(false);
    };
    window.addEventListener("keydown", handleDown);
    window.addEventListener("keyup", handleUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleDown);
      window.removeEventListener("keyup", handleUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  /* ---- polygon ---------------------------------------------------------- */

  const finishPolygon = () => {
    if (vertices.length >= 3) onAddRegion(polygonFrom(vertices, effect));
    setVertices([]);
  };
  const cancelPolygon = () => setVertices([]);
  const undoPolygonPoint = () => setVertices((current) => current.slice(0, -1));

  useImperativeHandle(ref, () => ({ finishPolygon, cancelPolygon, undoPolygonPoint }));

  // Layout effect so keyboard shortcuts see the polygon on the very next key.
  useLayoutEffect(() => {
    onPolygonChange(vertices.length);
  }, [onPolygonChange, vertices.length]);

  // Leaving the polygon tool or the image abandons an unfinished outline.
  const [polygonContext, setPolygonContext] = useState(`${tool}:${image.id}`);
  if (polygonContext !== `${tool}:${image.id}`) {
    setPolygonContext(`${tool}:${image.id}`);
    if (vertices.length > 0) setVertices([]);
  }

  /* ---- drafts ----------------------------------------------------------- */

  const draft = useMemo<MosaicRegion | null>(() => {
    if (gesture?.kind === "brush" && gesture.points.length > 0) {
      if (tool === "erase") {
        return { id: "draft", kind: "erase", points: gesture.points, penWidth };
      }
      const relative = toRelativePoints(gesture.points);
      return {
        id: "draft",
        kind: "stroke",
        x: relative.origin.x,
        y: relative.origin.y,
        rotation: 0,
        effect,
        points: relative.points,
        penWidth,
      };
    }
    if (gesture?.kind === "box" && tool === "shape") {
      const rect = normalizeRect(gesture.start, gesture.end, gesture.square);
      if (rect.width < 1 || rect.height < 1) return null;
      return { id: "draft", kind: shapeKind, ...rect, rotation: 0, effect };
    }
    if (gesture?.kind === "lasso" && gesture.dragging && gesture.points.length >= 3) {
      return { ...polygonFrom(gesture.points, effect), id: "draft" };
    }
    if (vertices.length >= 2) {
      const points = hover ? [...vertices, toImage(hover)] : vertices;
      return { ...polygonFrom(points, effect), id: "draft" };
    }
    return null;
  }, [effect, gesture, hover, penWidth, shapeKind, tool, toImage, vertices]);

  const onDraftChangeRef = useRef(onDraftChange);
  useLayoutEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);
  const draftFrameRef = useRef<number | null>(null);
  useEffect(() => {
    if (draftFrameRef.current !== null) cancelAnimationFrame(draftFrameRef.current);
    draftFrameRef.current = requestAnimationFrame(() => {
      draftFrameRef.current = null;
      onDraftChangeRef.current(draft);
    });
  }, [draft]);
  useEffect(
    () => () => {
      if (draftFrameRef.current !== null) cancelAnimationFrame(draftFrameRef.current);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  /* ---- live region updates --------------------------------------------- */

  const onUpdateRegionsRef = useRef(onUpdateRegions);
  useLayoutEffect(() => {
    onUpdateRegionsRef.current = onUpdateRegions;
  }, [onUpdateRegions]);

  const scheduleUpdate = (next: EditableRegion[]) => {
    pendingRef.current = next;
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      if (pendingRef.current.length > 0) onUpdateRegionsRef.current(pendingRef.current, false);
      pendingRef.current = [];
    });
  };

  const commitUpdate = (next: EditableRegion[]) => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    pendingRef.current = [];
    onUpdateRegionsRef.current(next, true);
  };

  const findNode = (id: string) => stageRef.current?.findOne(`#region-${id}`) as Konva.Shape | undefined;

  /**
   * Reads a node back into a region. Geometry comes from the node itself and
   * any transformer scale is folded in right away, so the node never carries
   * a scale and every later transform event starts from baked geometry.
   */
  const readNode = (id: string): EditableRegion | null => {
    const node = findNode(id);
    const region = regionsById.get(id);
    if (!node || !region) return null;
    const placed = { x: node.x(), y: node.y(), rotation: node.rotation() };
    let next: EditableRegion =
      region.kind === "stroke"
        ? { ...region, ...placed, points: pointsOfNode(node), penWidth: node.strokeWidth() }
        : region.kind === "polygon"
          ? { ...region, ...placed, points: pointsOfNode(node) }
          : { ...region, ...placed, width: node.width(), height: node.height() };
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    if (Math.abs(scaleX - 1) > 1e-6 || Math.abs(scaleY - 1) > 1e-6) {
      next = bakeScale(next, scaleX, scaleY);
      node.scale({ x: 1, y: 1 });
      if (next.kind === "stroke") {
        (node as Konva.Line).points(flatPoints(next.points));
        node.strokeWidth(next.penWidth);
      } else if (next.kind === "polygon") {
        (node as Konva.Line).points(flatPoints(next.points));
      } else {
        node.size({ width: next.width, height: next.height });
      }
    }
    return next;
  };

  /* ---- transformer ------------------------------------------------------ */

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) return;
    const nodes = selecting
      ? selectedIds.flatMap((id) => {
          const node = findNode(id);
          return node ? [node] : [];
        })
      : [];
    transformer.nodes(nodes);
    transformer.getLayer()?.batchDraw();
  }, [regions, selectedIds, selecting]);

  useEffect(() => {
    transformerRef.current?.forceUpdate();
  }, [view.scale, view.x, view.y, revision]);

  /* ---- pointer gestures ------------------------------------------------- */

  const screenPoint = (event: { clientX: number; clientY: number }): Point => {
    const box = containerRef.current?.getBoundingClientRect();
    return { x: event.clientX - (box?.left ?? 0), y: event.clientY - (box?.top ?? 0) };
  };

  const clampToImage = (point: Point): Point => ({
    x: Math.max(0, Math.min(image.width, point.x)),
    y: Math.max(0, Math.min(image.height, point.y)),
  });

  const imagePoint = (event: { clientX: number; clientY: number }) =>
    clampToImage(toImage(screenPoint(event)));

  const startPinch = () => {
    const [first, second] = [...touchesRef.current.values()];
    // A second finger means navigating, not drawing.
    setGesture(null);
    pinchRef.current = {
      distance: distance(first, second),
      center: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
      zoom,
      pan,
    };
  };

  const handleStagePointerDown = (event: Konva.KonvaEventObject<PointerEvent>) => {
    const native = event.evt;
    const screen = screenPoint(native);
    if (native.pointerType === "touch") {
      touchesRef.current.set(native.pointerId, screen);
      if (touchesRef.current.size === 2) {
        startPinch();
        return;
      }
      if (touchesRef.current.size > 2) return;
    }
    containerRef.current?.focus({ preventScroll: true });

    const panButton = native.button === 1 || native.button === 2;
    if (panButton || spaceDown || !editing) {
      if (native.button === 0 || panButton) {
        setGesture({ kind: "pan", origin: screen, startPan: pan });
      }
      return;
    }
    if (native.button !== 0) return;

    const point = clampToImage(toImage(screen));
    switch (tool) {
      case "select":
        setGesture({
          kind: "marquee",
          start: point,
          end: point,
          mode: native.shiftKey ? "add" : "replace",
        });
        break;
      case "shape":
      case "restore":
        setGesture({ kind: "box", start: point, end: point, square: native.shiftKey });
        break;
      case "pen":
      case "erase":
        setGesture({ kind: "brush", points: [point] });
        break;
      case "polygon":
        setGesture(
          vertices.length === 0
            ? { kind: "lasso", origin: screen, points: [point], dragging: false }
            : { kind: "vertex", origin: screen },
        );
        break;
    }
  };

  const handlePointerMove = (native: PointerEvent) => {
    const screen = screenPoint(native);
    if (native.pointerType === "touch" && touchesRef.current.has(native.pointerId)) {
      touchesRef.current.set(native.pointerId, screen);
      const pinch = pinchRef.current;
      if (pinch && touchesRef.current.size >= 2) {
        const [first, second] = [...touchesRef.current.values()];
        const center = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
        const nextZoom = clampZoom(pinch.zoom * (distance(first, second) / pinch.distance));
        // Pinch zooms about the original centre and pans with the fingers.
        zoomAround(
          { x: center.x, y: center.y },
          nextZoom,
          {
            zoom: pinch.zoom,
            pan: {
              x: pinch.pan.x + center.x - pinch.center.x,
              y: pinch.pan.y + center.y - pinch.center.y,
            },
          },
        );
        return;
      }
    }

    const current = gestureRef.current;
    if (!current) return;
    if (current.kind === "pan") {
      setPan({
        x: current.startPan.x + screen.x - current.origin.x,
        y: current.startPan.y + screen.y - current.origin.y,
      });
      return;
    }
    const point = clampToImage(toImage(screen));
    if (current.kind === "marquee") {
      setGesture({ ...current, end: point });
    } else if (current.kind === "box") {
      setGesture({ ...current, end: point, square: native.shiftKey });
    } else if (current.kind === "brush") {
      const last = current.points[current.points.length - 1];
      if (distance(last, point) * view.scale >= 1.5) {
        setGesture({ ...current, points: [...current.points, point] });
      }
    } else if (current.kind === "lasso") {
      const dragging = current.dragging || distance(screen, current.origin) > CLICK_SLOP;
      const last = current.points[current.points.length - 1];
      if (dragging && distance(last, point) * view.scale >= 2) {
        setGesture({ ...current, dragging, points: [...current.points, point] });
      }
    }
  };

  const finishGesture = (native: PointerEvent) => {
    if (native.pointerType === "touch") {
      touchesRef.current.delete(native.pointerId);
      if (pinchRef.current) {
        if (touchesRef.current.size === 0) pinchRef.current = null;
        return;
      }
    }
    const current = gestureRef.current;
    if (!current) return;
    setGesture(null);
    const screen = screenPoint(native);

    if (current.kind === "marquee") {
      const rect = normalizeRect(current.start, current.end);
      if (rect.width * view.scale < CLICK_SLOP && rect.height * view.scale < CLICK_SLOP) {
        if (current.mode === "replace") onSelect([], "replace");
        return;
      }
      onSelect(
        regions.filter((region) => intersects(regionBounds(region), rect)).map(({ id }) => id),
        current.mode,
      );
    } else if (current.kind === "box") {
      const rect = normalizeRect(current.start, current.end, current.square);
      onDraftChange(null);
      if (rect.width * view.scale < 4 || rect.height * view.scale < 4) return;
      if (tool === "restore") {
        onRequestRestore(rect);
      } else {
        onAddRegion({ id: crypto.randomUUID(), kind: shapeKind, ...rect, rotation: 0, effect });
      }
    } else if (current.kind === "brush") {
      onDraftChange(null);
      const points = simplifyPoints(current.points, 1 / view.scale);
      if (tool === "erase") {
        onAddRegion({ id: crypto.randomUUID(), kind: "erase", points, penWidth });
      } else {
        const relative = toRelativePoints(points);
        onAddRegion({
          id: crypto.randomUUID(),
          kind: "stroke",
          x: relative.origin.x,
          y: relative.origin.y,
          rotation: 0,
          effect,
          points: relative.points,
          penWidth,
        });
      }
    } else if (current.kind === "lasso") {
      onDraftChange(null);
      if (current.dragging && current.points.length >= 3) {
        onAddRegion(polygonFrom(simplifyPoints(current.points, 2 / view.scale), effect));
      } else {
        setVertices([current.points[0]]);
      }
    } else if (current.kind === "vertex") {
      if (distance(screen, current.origin) > CLICK_SLOP * 3) return;
      const point = imagePoint(native);
      const first = vertices[0];
      const firstScreen = {
        x: view.x + first.x * view.scale,
        y: view.y + first.y * view.scale,
      };
      if (vertices.length >= 3 && distance(screen, firstScreen) <= CLOSE_RADIUS) {
        finishPolygon();
        return;
      }
      const last = vertices[vertices.length - 1];
      // A second click on the last vertex is a double-click: finish there.
      if (distance(last, point) * view.scale < CLICK_SLOP) {
        if (vertices.length >= 3) finishPolygon();
        return;
      }
      setVertices([...vertices, point]);
    }
  };

  const handleHover = (native: PointerEvent) => {
    const screen = screenPoint(native);
    const ring = brushRingRef.current;
    if (ring) ring.style.transform = `translate(${screen.x}px, ${screen.y}px)`;
    if (tool === "polygon" && vertices.length > 0) setHover(screen);
  };

  // Window listeners keep a drag alive when the pointer leaves the canvas.
  const handlersRef = useRef({ handlePointerMove, finishGesture, handleHover });
  useLayoutEffect(() => {
    handlersRef.current = { handlePointerMove, finishGesture, handleHover };
  });
  useEffect(() => {
    const move = (event: PointerEvent) => {
      handlersRef.current.handlePointerMove(event);
      handlersRef.current.handleHover(event);
    };
    const up = (event: PointerEvent) => handlersRef.current.finishGesture(event);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  useEffect(() => {
    if (vertices.length === 0) setHover(null);
  }, [vertices.length]);

  const handleWheel = (event: Konva.KonvaEventObject<WheelEvent>) => {
    const native = event.evt;
    native.preventDefault();
    // Plain scrolling pans and a pinch (ctrlKey) or Ctrl/Cmd+wheel zooms,
    // matching Canva and Figma on both trackpads and mice.
    if (!native.ctrlKey && !native.metaKey) {
      const dx = native.shiftKey && native.deltaX === 0 ? native.deltaY : native.deltaX;
      const dy = native.shiftKey && native.deltaX === 0 ? 0 : native.deltaY;
      setPan((current) => ({ x: current.x - dx, y: current.y - dy }));
      return;
    }
    const factor = Math.min(1.25, Math.max(0.8, Math.exp(-native.deltaY * 0.01)));
    zoomAround(screenPoint(native), zoom * factor);
  };

  /* ---- region interaction ---------------------------------------------- */

  const regionHandlers = (region: EditableRegion) => ({
    onPointerDown: (event: Konva.KonvaEventObject<PointerEvent>) => {
      if (event.evt.button !== 0 || spaceDown) return;
      event.cancelBubble = true;
      containerRef.current?.focus({ preventScroll: true });
      if (event.evt.shiftKey) onSelect([region.id], "toggle");
      // Keep a multi-selection when pressing one of its members, so it drags as a group.
      else if (!selectedSet.has(region.id)) onSelect([region.id], "replace");
    },
    onPointerEnter: () => setHoveredId(region.id),
    onPointerLeave: () => setHoveredId((current) => (current === region.id ? null : current)),
    onDragStart: (event: Konva.KonvaEventObject<DragEvent>) => {
      onBeginEdit();
      const ids = selectedSet.has(region.id) ? selectedIds : [region.id];
      const positions = new Map<string, Point>();
      ids.forEach((id) => {
        const node = findNode(id);
        if (node) positions.set(id, node.position());
      });
      groupDragRef.current = { origin: event.target.position(), positions };
    },
    onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => {
      const drag = groupDragRef.current;
      if (!drag) return;
      const dx = event.target.x() - drag.origin.x;
      const dy = event.target.y() - drag.origin.y;
      drag.positions.forEach((position, id) => {
        if (id !== region.id) findNode(id)?.position({ x: position.x + dx, y: position.y + dy });
      });
      scheduleUpdate([...drag.positions.keys()].flatMap((id) => readNode(id) ?? []));
    },
    onDragEnd: () => {
      const ids = groupDragRef.current ? [...groupDragRef.current.positions.keys()] : [region.id];
      groupDragRef.current = null;
      commitUpdate(ids.flatMap((id) => readNode(id) ?? []));
    },
  });

  const outlineFor = (region: EditableRegion): OutlineState => {
    if (!editing) return "hidden";
    if (tool === "select" && selectedSet.has(region.id)) return "selected";
    if (tool === "select" && hoveredId === region.id) return "hover";
    return showOutlines ? "idle" : "hidden";
  };

  /* ---- render ------------------------------------------------------------ */

  const gridLines = useMemo(() => {
    if (!showGrid) return [];
    const [columns, rows] = cellCounts(image.width, image.height, gridSize);
    const columnStep = Math.max(1, Math.ceil(columns / 150));
    const rowStep = Math.max(1, Math.ceil(rows / 150));
    return [
      ...cellEdges(image.width, columns)
        .filter((_, index) => index % columnStep === 0)
        .map((x) => [x, 0, x, image.height]),
      ...cellEdges(image.height, rows)
        .filter((_, index) => index % rowStep === 0)
        .map((y) => [0, y, image.width, y]),
    ];
  }, [gridSize, image.height, image.width, showGrid]);

  const unit = 1 / view.scale;
  const marquee =
    gesture?.kind === "marquee" ? normalizeRect(gesture.start, gesture.end) : null;
  const restoreBox =
    gesture?.kind === "box" && tool === "restore"
      ? normalizeRect(gesture.start, gesture.end, gesture.square)
      : null;
  const draftOutline =
    draft && draft.kind !== "erase" && draft.id === "draft" ? draft : null;
  const showBrushRing =
    pointerInside && editing && (tool === "pen" || tool === "erase") && !spaceDown;
  const panning = gesture?.kind === "pan";
  const firstVertexHot =
    vertices.length >= 3 &&
    hover !== null &&
    distance(hover, {
      x: view.x + vertices[0].x * view.scale,
      y: view.y + vertices[0].y * view.scale,
    }) <= CLOSE_RADIUS;

  const cursor = panning
    ? "grabbing"
    : spaceDown || !editing
      ? "grab"
      : tool === "select"
        ? hoveredId
          ? "move"
          : "default"
        : tool === "pen" || tool === "erase"
          ? "none"
          : firstVertexHot
            ? "pointer"
            : "crosshair";

  return (
    <div
      ref={containerRef}
      className="editor-canvas"
      style={{ cursor }}
      tabIndex={-1}
      role="application"
      aria-label="画像編集キャンバス"
      aria-roledescription="キャンバス"
      data-view-scale={view.scale.toFixed(6)}
      data-view-x={view.x.toFixed(2)}
      data-view-y={view.y.toFixed(2)}
      data-preview-revision={revision}
      data-region-count={regions.length}
      data-selected-count={selectedIds.length}
      onPointerEnter={(event) => {
        if (event.pointerType !== "touch") setPointerInside(true);
      }}
      onPointerLeave={() => {
        setPointerInside(false);
        setHoveredId(null);
        if (tool === "polygon") setHover(null);
      }}
    >
      <Stage
        ref={stageRef}
        width={size.width}
        height={size.height}
        onPointerDown={handleStagePointerDown}
        onWheel={handleWheel}
        onContextMenu={(event) => event.evt.preventDefault()}
      >
        <Layer listening={false}>
          <Group x={view.x} y={view.y} scaleX={view.scale} scaleY={view.scale}>
            <Rect
              width={image.width}
              height={image.height}
              fill="#ffffff"
              shadowColor="#0b1414"
              shadowOpacity={0.16}
              shadowBlur={24 * unit}
              shadowOffsetY={6 * unit}
            />
            {shownImage && (
              <KonvaImage image={shownImage} width={image.width} height={image.height} />
            )}
            {restorePreview && restoredImage && !comparing && (
              <>
                <Group
                  clipX={restorePreview.x + restorePreview.width * restorePreview.compare}
                  clipY={restorePreview.y}
                  clipWidth={restorePreview.width * (1 - restorePreview.compare)}
                  clipHeight={restorePreview.height}
                >
                  <KonvaImage
                    image={restoredImage}
                    x={restorePreview.x}
                    y={restorePreview.y}
                    width={restorePreview.width}
                    height={restorePreview.height}
                    opacity={restorePreview.strength / 100}
                  />
                </Group>
                <Rect
                  x={restorePreview.x}
                  y={restorePreview.y}
                  width={restorePreview.width}
                  height={restorePreview.height}
                  stroke="#ffffff"
                  strokeWidth={1.5 * unit}
                  dash={[6 * unit, 4 * unit]}
                />
                <Line
                  points={[
                    restorePreview.x + restorePreview.width * restorePreview.compare,
                    restorePreview.y,
                    restorePreview.x + restorePreview.width * restorePreview.compare,
                    restorePreview.y + restorePreview.height,
                  ]}
                  stroke="#ffffff"
                  strokeWidth={2 * unit}
                />
                <Text
                  text="元画像"
                  x={restorePreview.x + 8 * unit}
                  y={restorePreview.y + 8 * unit}
                  fontSize={12 * unit}
                  fill="#ffffff"
                  shadowColor="#000"
                  shadowBlur={4 * unit}
                />
                <Text
                  text="復元結果"
                  x={restorePreview.x + restorePreview.width - 60 * unit}
                  y={restorePreview.y + 8 * unit}
                  fontSize={12 * unit}
                  fill="#ffffff"
                  shadowColor="#000"
                  shadowBlur={4 * unit}
                />
              </>
            )}
            {gridLines.map((points, index) => (
              <Line key={index} points={points} stroke="rgba(255,255,255,.35)" strokeWidth={unit} />
            ))}
          </Group>
        </Layer>

        <Layer>
          <Group x={view.x} y={view.y} scaleX={view.scale} scaleY={view.scale}>
            {regions.map((region) => (
              <RegionNode
                key={region.id}
                region={region}
                outline={outlineFor(region)}
                listening={selecting && !spaceDown}
                draggable={selecting && !spaceDown}
                minHitWidth={16 * unit}
                {...regionHandlers(region)}
              />
            ))}
            {draftOutline && (
              <RegionNode
                region={draftOutline}
                outline="selected"
                listening={false}
                draggable={false}
                minHitWidth={0}
              />
            )}
            {gesture?.kind === "brush" && tool === "erase" && (
              <Line
                points={flatPoints(gesture.points)}
                stroke="rgba(255,255,255,.45)"
                strokeWidth={penWidth}
                lineCap="round"
                lineJoin="round"
                listening={false}
              />
            )}
          </Group>
        </Layer>

        <Layer>
          <Group x={view.x} y={view.y} scaleX={view.scale} scaleY={view.scale} listening={false}>
            {marquee && (
              <Rect
                {...marquee}
                stroke="#287b86"
                strokeWidth={1.25 * unit}
                dash={[5 * unit, 4 * unit]}
                fill="rgba(40,123,134,.08)"
              />
            )}
            {restoreBox && (
              <Rect
                {...restoreBox}
                stroke={SELECTION_COLOR}
                strokeWidth={2 * unit}
                dash={[6 * unit, 4 * unit]}
                fill="rgba(255,101,79,.08)"
              />
            )}
            {vertices.length > 0 && (
              <>
                {vertices.length === 1 && hover && (
                  <Line
                    points={[vertices[0].x, vertices[0].y, toImage(hover).x, toImage(hover).y]}
                    stroke={SELECTION_COLOR}
                    strokeWidth={1.5 * unit}
                    dash={[5 * unit, 4 * unit]}
                  />
                )}
                {vertices.map((vertex, index) => (
                  <Circle
                    key={index}
                    x={vertex.x}
                    y={vertex.y}
                    radius={(index === 0 && firstVertexHot ? 7 : 4.5) * unit}
                    fill={index === 0 ? SELECTION_COLOR : "#ffffff"}
                    stroke={index === 0 ? "#ffffff" : SELECTION_COLOR}
                    strokeWidth={1.5 * unit}
                  />
                ))}
              </>
            )}
          </Group>
          <Transformer
            ref={transformerRef}
            visible={selecting}
            rotateEnabled
            flipEnabled={false}
            keepRatio={shiftDown || selectedIds.length > 1}
            rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
            rotationSnapTolerance={4}
            anchorSize={11}
            anchorCornerRadius={6}
            anchorStroke={SELECTION_COLOR}
            anchorStrokeWidth={1.5}
            anchorFill="#ffffff"
            borderStroke={SELECTION_COLOR}
            borderStrokeWidth={1.5}
            rotateAnchorOffset={26}
            onTransformStart={onBeginEdit}
            onTransform={() => scheduleUpdate(selectedIds.flatMap((id) => readNode(id) ?? []))}
            onTransformEnd={() => {
              commitUpdate(selectedIds.flatMap((id) => readNode(id) ?? []));
              transformerRef.current?.forceUpdate();
            }}
            boundBoxFunc={(oldBox, newBox) =>
              Math.abs(newBox.width) < 6 || Math.abs(newBox.height) < 6 ? oldBox : newBox
            }
          />
        </Layer>
      </Stage>

      {showBrushRing && (
        <div
          ref={brushRingRef}
          className={`brush-ring ${tool === "erase" ? "is-eraser" : ""}`}
          aria-hidden="true"
        >
          <span
            style={{ width: penWidth * view.scale, height: penWidth * view.scale }}
          />
        </div>
      )}
      {brushPreview && (tool === "pen" || tool === "erase") && (
        <div
          className="brush-size-preview"
          style={{ width: penWidth * view.scale, height: penWidth * view.scale }}
          data-diameter={(penWidth * view.scale).toFixed(2)}
          aria-hidden="true"
        />
      )}
      {!shownImage && (
        <div className="canvas-loading" role="status">
          画像を準備しています…
        </div>
      )}
    </div>
  );
}
